import './index.css';
import { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import useCursor from './hooks/useCursor';
import useSmoothScroll from './hooks/useSmoothScroll';
import Preloader from './components/Preloader';
import Nav    from './components/Nav';
import Footer from './components/Footer';
import Hero   from './sections/Hero';
import WhoWeAre from './sections/WhoWeAre';
import Solutions from './sections/Solutions';
import WhyChooseUs from './sections/WhyChooseUs';
import About  from './sections/About';
import Enquiry from './sections/Enquiry';
import Contact from './sections/Contact';

export default function App() {
  const { dot, ring } = useCursor();
  useSmoothScroll();
  const [loaded, setLoaded] = useState(false);
  const chromeRef = useRef(null);
  const [logoHidden, setLogoHidden] = useState(false);

  // Fade the fixed chrome (logo + nav + rail) in once the preloader lifts
  useEffect(() => {
    if (!loaded || !chromeRef.current) return;
    gsap.fromTo(
      chromeRef.current.children,
      { opacity: 0, y: -8 },
      {
        opacity: 1, y: 0, duration: 0.9, ease: 'power2.out', stagger: 0.12, delay: 0.2,
        // Drop the inline opacity/transform GSAP leaves behind — otherwise it
        // outranks the .logo--hidden class and the scroll retract never shows.
        clearProps: 'opacity,transform',
      }
    );
  }, [loaded]);

  // Logo retracts on scroll down, comes back on scroll up. Read on rAF because
  // Lenis fires scroll every frame and we only want one state flip per change.
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const delta = y - last;
      // Ignore sub-pixel jitter and momentum wobble at the very top
      if (Math.abs(delta) > 6) {
        setLogoHidden(delta > 0 && y > 120);
        last = y;
      } else if (y <= 120) {
        setLogoHidden(false);
      }
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <div className="bg-pattern" aria-hidden="true" />

      <div className="cursor-dot"  ref={dot}  />
      <div className="cursor-ring" ref={ring} />

      {!loaded && <Preloader onDone={() => setLoaded(true)} />}

      <div ref={chromeRef} style={{ opacity: loaded ? undefined : 0 }}>
        <a href="#home" className={`logo${logoHidden ? ' logo--hidden' : ''}`}>
          <span className="logo-mark" />C-Solar
        </a>
        <Nav />
      </div>

      <main>
        <Hero start={loaded} />
        <WhoWeAre />
        <Solutions />
        <WhyChooseUs />
        <About />
        <Enquiry />
        <Contact />
        <Footer />
      </main>
    </>
  );
}
